import { useState, useEffect } from "react";
import api, { errMsg, limitInfo } from "@/lib/api";
import { Eyebrow } from "@/components/Eyebrow";
import { BlueprintCard } from "@/components/BlueprintCard";
import { InfoTip } from "@/components/InfoTip";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "@/components/ui/sonner";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2, ImageIcon, Copy, Rocket, Tag, Camera, Quote, Lightbulb, Megaphone, Check, RotateCw, Facebook, Instagram, Music2 } from "lucide-react";
import { copyCaption, lbToast } from "@/lib/util";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "tiktok", label: "TikTok", icon: Music2 },
];
const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

const TEMPLATES = [
  { id: "launch", label: "Product Launch", icon: Rocket, assetType: "Social Post", text: "Announce our new product launch. Lead with a bold hero visual, highlight the single most exciting benefit, and build anticipation. Confident, premium tone." },
  { id: "promo", label: "Sale / Promo", icon: Tag, assetType: "Ad Creative", text: "Promote a limited-time offer. Make the discount unmissable, create urgency with a clear deadline, and drive immediate action. Energetic, punchy tone." },
  { id: "bts", label: "Behind the Scenes", icon: Camera, assetType: "Instagram Carousel", text: "Take followers behind the scenes of how we work. Authentic, human and story-driven — show the people, process and craft across the slides." },
  { id: "testimonial", label: "Testimonial", icon: Quote, assetType: "Social Post", text: "Spotlight a glowing customer testimonial. Feature the quote prominently against supporting brand visuals. Warm, trustworthy, social-proof tone." },
  { id: "tip", label: "Educational Tip", icon: Lightbulb, assetType: "Instagram Carousel", text: "Teach the audience one genuinely useful tip from our industry. Clear, value-first and swipeable across slides. Helpful, expert tone." },
  { id: "announcement", label: "Announcement", icon: Megaphone, assetType: "LinkedIn Post", text: "Share an important company milestone or announcement. Professional, celebratory and credible with a clean editorial layout." },
];

function IdeaCard({ idea, index, assetType, onChange, onRender, rendering, renderCount }) {
  const rendered = renderCount > 0;
  const MAX_RENDERS = 3; // 1 initial + up to 2 re-renders
  const canRerender = renderCount < MAX_RENDERS;
  const rerendersLeft = MAX_RENDERS - renderCount;

  const platforms = (idea.platforms && idea.platforms.length ? idea.platforms : ["instagram"]);
  const active = platforms.includes(idea.activePlatform) ? idea.activePlatform : platforms[0];
  const captions = idea.captions || { [active]: idea.caption || "" };
  const activeCaption = captions[active] ?? idea.caption ?? "";

  const setActive = (p) => onChange({ ...idea, activePlatform: p, caption: captions[p] ?? "" });
  const editActive = (val) => onChange({ ...idea, caption: val, captions: { ...captions, [active]: val } });

  return (
    <BlueprintCard index={index} testId={`idea-card-${index}`} style={{ padding: 18, marginBottom: 16 }}>
      <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, color: "var(--tprimary)", margin: 0, letterSpacing: "-0.01em" }}>
        {idea.title}
      </h3>
      <p className="font-body" style={{ fontSize: 14, color: "var(--tsecondary)", margin: "8px 0 14px", lineHeight: 1.55 }}>
        {idea.visualDirection}
      </p>

      {platforms.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }} data-testid={`platform-tabs-${index}`}>
          {platforms.map((p) => {
            const PI = (PLATFORM_MAP[p] || {}).icon || Tag;
            const on = p === active;
            return (
              <button
                key={p}
                data-testid={`platform-tab-${index}-${p}`}
                onClick={() => setActive(p)}
                className="font-mono"
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 999,
                  background: on ? "var(--orange)" : "transparent",
                  color: on ? "var(--surface)" : "var(--tsecondary)",
                  border: on ? "none" : "1px solid rgba(24, 24, 27,0.20)",
                  fontSize: 11, cursor: "pointer", textTransform: "capitalize",
                }}
              >
                <PI size={12} /> {(PLATFORM_MAP[p] || {}).label || p}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
        <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {(PLATFORM_MAP[active] || {}).label || "Caption"} caption
        </label>
        <button
          data-testid={`copy-caption-${index}`}
          onClick={() => copyCaption(activeCaption)}
          title="Copy caption"
          className="font-mono"
          style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "var(--tmuted)", cursor: "pointer", fontSize: 10, letterSpacing: "0.06em" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--orange)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--tmuted)")}
        >
          <Copy size={12} /> COPY
        </button>
      </div>
      <textarea
        data-testid={`idea-caption-${index}`}
        className="dim-input font-body"
        style={{ marginTop: 6, minHeight: 70 }}
        value={activeCaption}
        onChange={(e) => editActive(e.target.value)}
      />

      <div className="font-mono" style={{ fontSize: 11, color: "var(--orange)", marginTop: 10, lineHeight: 1.6 }} data-testid={`idea-hashtags-${index}`}>
        {(idea.hashtags || []).join(" ")}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)" }}>SLIDES</span>
          {[3, 5].map((n) => (
            <button
              key={n}
              data-testid={`idea-slides-${index}-${n}`}
              onClick={() => onChange({ ...idea, slides: n })}
              className="font-mono"
              style={{
                width: 30, height: 28, borderRadius: 6, cursor: "pointer",
                background: idea.slides === n ? "var(--orange)" : "transparent",
                color: idea.slides === n ? "var(--surface)" : "var(--tsecondary)",
                border: idea.slides === n ? "none" : "1px solid rgba(24, 24, 27,0.20)",
                fontSize: 12, fontWeight: 600,
              }}
            >
              {n}
            </button>
          ))}
        </div>
        {!rendered ? (
          <button data-testid={`render-assets-${index}`} className="btn-primary" onClick={() => onRender(idea)} disabled={rendering}>
            {rendering ? <Loader2 size={14} className="spin" /> : <ImageIcon size={14} />}
            {rendering ? "Rendering..." : "Render Assets"}
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              data-testid={`render-assets-${index}`}
              disabled
              className="font-body"
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 8,
                background: "var(--ok)", color: "#0D0D0D", border: "none", fontWeight: 600, fontSize: 13,
                cursor: "default", opacity: rendering ? 0.85 : 1,
              }}
            >
              {rendering ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
              {rendering ? "Rendering..." : "Rendered"}
            </button>
            <button
              data-testid={`rerender-assets-${index}`}
              onClick={() => canRerender && !rendering && onRender(idea)}
              disabled={rendering || !canRerender}
              title={canRerender ? `Re-render (${rerendersLeft} left)` : "Re-render limit reached"}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", width: 38, height: 38,
                borderRadius: 8, background: "transparent",
                border: "1px solid rgba(24, 24, 27,0.30)",
                color: canRerender ? "var(--orange)" : "var(--tmuted)",
                cursor: canRerender && !rendering ? "pointer" : "not-allowed",
                opacity: canRerender ? 1 : 0.5,
              }}
            >
              <RotateCw size={15} />
            </button>
          </div>
        )}
      </div>
    </BlueprintCard>
  );
}

export function StudioPanel({ brandDna, brandId, ideas, setIdeas, assetTypes, seedDirection, addImage, updateImage, removeImage, clearImages, hasPreviews, onLimit, refreshUsage }) {
  const [direction, setDirection] = useState("");
  const [assetType, setAssetType] = useState("Social Post");
  const [count, setCount] = useState(3);
  const [platforms, setPlatforms] = useState(["instagram"]);
  const [textStyle, setTextStyle] = useState("overlay");
  const [generating, setGenerating] = useState(false);
  const [renderingIds, setRenderingIds] = useState(() => new Set());
  const [renderCounts, setRenderCounts] = useState({});

  useEffect(() => {
    if (seedDirection?.text) {
      setDirection(seedDirection.text);
      setTimeout(() => {
        document.querySelector('[data-testid="creative-direction"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    }
  }, [seedDirection]);

  const togglePlatform = (id) => {
    setPlatforms((prev) => {
      if (prev.includes(id)) return prev.length === 1 ? prev : prev.filter((p) => p !== id);
      return [...prev, id];
    });
  };

  const types = assetTypes && assetTypes.length ? assetTypes : ["Instagram Carousel", "Social Post", "Ad Creative", "LinkedIn Post", "Story Cover"];

  const applyTemplate = (t) => {
    setDirection(t.text);
    if (types.includes(t.assetType)) setAssetType(t.assetType);
    lbToast(`Template applied: ${t.label}`);
  };

  const generate = async () => {
    if (!brandDna) { toast("Analyze a brand first", { description: "Use the Brand DNA panel on the left." }); return; }
    if (!direction.trim()) { toast("Add a creative direction"); return; }
    if (hasPreviews) lbToast("Previous renders cleared");
    clearImages?.();
    setGenerating(true);
    try {
      const { data } = await api.post("/ideas/generate", {
        brandDna, brandId, creativeDirection: direction.trim(), assetType, count, platforms,
      });
      setIdeas((data.ideas || []).map((d, i) => ({
        ...d,
        id: d.id || `idea-${i}-${Date.now()}`,
        slides: 3,
        activePlatform: (d.platforms && d.platforms[0]) || "instagram",
      })));
      setRenderCounts({});
      setRenderingIds(new Set());
      refreshUsage?.();
      setTimeout(() => {
        document.querySelector('[data-testid="ideas-list"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } catch (e) {
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Idea generation failed"));
    } finally {
      setGenerating(false);
    }
  };

  const updateIdea = (next) => setIdeas((prev) => prev.map((it) => (it.id === next.id ? next : it)));

  const renderAssets = async (idea) => {
    if (renderingIds.has(idea.id)) return;
    const currentCount = renderCounts[idea.id] || 0;
    if (currentCount >= 3) {
      toast("Re-render limit reached", { description: "A post can be rendered up to 3 times (1 + 2 re-renders)." });
      return;
    }
    setRenderingIds((prev) => new Set(prev).add(idea.id));
    const finishRendering = () =>
      setRenderingIds((prev) => {
        const n = new Set(prev);
        n.delete(idea.id);
        return n;
      });

    const tempId = addImage({
      caption: idea.caption,
      captions: idea.captions || { [idea.activePlatform || "instagram"]: idea.caption },
      platforms: idea.platforms || [idea.activePlatform || "instagram"],
      brandId,
      colors: brandDna?.colors || [],
      siteName: brandDna?.siteName || "",
      assetType,
      visualDirection: idea.visualDirection,
      title: idea.title,
      slides: idea.slides,
      items: [],
      status: "rendering",
    });
    try {
      const { data } = await api.post("/images/render", {
        visualDirection: idea.visualDirection,
        caption: idea.caption,
        assetType,
        colors: brandDna?.colors || [],
        siteName: brandDna?.siteName || "",
        slides: idea.slides,
        title: idea.title,
        brandId,
        textStyle,
        headline: idea.title,
      });
      refreshUsage?.();
      const groupId = data.groupId;
      updateImage(tempId, { groupId, aspect: data.aspect });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data: g } = await api.get(`/images/group/${groupId}`);
          updateImage(tempId, { items: g.items, aspect: g.aspect, status: g.status === "done" ? "done" : "rendering" });
          if (g.status === "rendering" && attempts < 45) {
            setTimeout(poll, 2000);
          } else if (g.status === "error") {
            removeImage(tempId);
            finishRendering();
            toast.error("Image render failed");
          } else if (g.status === "done") {
            finishRendering();
            setRenderCounts((prev) => ({ ...prev, [idea.id]: (prev[idea.id] || 0) + 1 }));
            toast.success(`Rendered ${g.items.length} ${g.items.length > 1 ? "slides" : "asset"}`, { description: idea.title });
          } else {
            updateImage(tempId, { status: "done" });
            finishRendering();
          }
        } catch (err) {
          if (attempts < 45) setTimeout(poll, 2500);
          else {
            updateImage(tempId, { status: "done" });
            finishRendering();
          }
        }
      };
      poll();
    } catch (e) {
      removeImage(tempId);
      finishRendering();
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Image render failed"));
    }
  };

  return (
    <div className={`thin-scroll ${generating ? "grid-pulse" : ""}`} style={{ padding: 20, height: "100%", overflowY: "auto" }} data-testid="studio-panel">
      <Eyebrow testId="eyebrow-studio">Studio</Eyebrow>

      <div style={{ marginTop: 18 }} data-testid="smart-templates">
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
          <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Smart Templates</label>
          <InfoTip testId="tip-templates" text="One-click starting points. Pick a template to pre-fill the creative direction and asset type, then tweak as you like." />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {TEMPLATES.map((t) => {
            const TIcon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`template-${t.id}`}
                onClick={() => applyTemplate(t)}
                className="font-body"
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 999,
                  background: "transparent", border: "1px solid rgba(24, 24, 27,0.20)", color: "var(--tsecondary)",
                  fontSize: 12, cursor: "pointer", transition: "border-color 0.2s ease, color 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--orange)"; e.currentTarget.style.borderColor = "var(--orange)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tsecondary)"; e.currentTarget.style.borderColor = "rgba(24, 24, 27,0.20)"; }}
              >
                <TIcon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Creative Direction</label>
          <InfoTip testId="tip-direction" text="Describe the campaign, mood or message in plain language. The more specific you are, the sharper the AI concepts." />
        </div>
        <textarea
          data-testid="creative-direction"
          className="dim-input font-body"
          style={{ marginTop: 6, minHeight: 96 }}
          placeholder="Describe the campaign, mood, or message you want to create..."
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
        />
      </div>

      <div style={{ marginTop: 16 }} data-testid="platform-selector">
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
          <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Platforms</label>
          <InfoTip testId="tip-platforms" text="Pick the platforms to post on. Each idea gets a caption tailored to every selected platform's format and tone." />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {PLATFORMS.map((p) => {
            const PI = p.icon;
            const on = platforms.includes(p.id);
            return (
              <button
                key={p.id}
                data-testid={`platform-toggle-${p.id}`}
                onClick={() => togglePlatform(p.id)}
                className="font-body"
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999,
                  background: on ? "var(--orange)" : "transparent",
                  color: on ? "var(--surface)" : "var(--tsecondary)",
                  border: on ? "none" : "1px solid rgba(24, 24, 27,0.20)",
                  fontSize: 12.5, cursor: "pointer", fontWeight: on ? 600 : 400,
                  transition: "background-color 0.2s ease, color 0.2s ease",
                }}
              >
                <PI size={14} /> {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 16 }} data-testid="text-style-selector">
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
          <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Rendered text style</label>
          <InfoTip testId="tip-textstyle" text="Overlay = crisp English text composited on a text-free image (recommended). Artistic = text rendered inside the image by a premium model." />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[["overlay", "Overlay (crisp)"], ["artistic", "Artistic (in-image)"]].map(([id, label]) => {
            const on = textStyle === id;
            return (
              <button
                key={id}
                data-testid={`textstyle-${id}`}
                onClick={() => setTextStyle(id)}
                className="font-body"
                style={{
                  padding: "7px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
                  background: on ? "var(--orange)" : "transparent",
                  color: on ? "var(--surface)" : "var(--tsecondary)",
                  border: on ? "none" : "1px solid rgba(24, 24, 27,0.20)", fontWeight: on ? 600 : 400,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        {textStyle === "artistic" && (
          <div className="font-body" style={{ fontSize: 11, color: "var(--tmuted)", marginTop: 6 }}>Uses premium rendering.</div>
        )}
      </div>


      <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 170 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
            <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Asset Type</label>
            <InfoTip testId="tip-asset-type" text="The format you're creating for. This sets the aspect ratio and tailors the layout (e.g. square posts, vertical stories, wide LinkedIn banners)." />
          </div>
          <Select value={assetType} onValueChange={setAssetType}>
            <SelectTrigger data-testid="asset-type-select" className="font-body" style={{ background: "var(--input)", border: "1px solid rgba(24, 24, 27,0.15)", borderRadius: 8, color: "var(--tprimary)" }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {types.map((t) => (
                <SelectItem key={t} value={t} data-testid={`asset-type-${t}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div style={{ flex: 1, minWidth: 150 }}>
          <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 10 }}>
            Ideas: <span style={{ color: "var(--orange)" }}>{count}</span>
          </label>
          <Slider data-testid="ideas-slider" min={1} max={5} step={1} value={[count]} onValueChange={(v) => setCount(v[0])} />
        </div>

        <button data-testid="generate-ideas-button" className="btn-primary" onClick={generate} disabled={generating} style={{ height: 38 }}>
          {generating ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
          {generating ? "Thinking..." : "Generate Ideas"}
        </button>
      </div>

      <div style={{ marginTop: 26 }} data-testid="ideas-list">
        {generating && (
          <div className="font-mono" style={{ fontSize: 12, color: "var(--orange)" }} data-testid="thinking-readout">
            DRAFTING CONCEPTS...
          </div>
        )}
        {!generating && ideas.length === 0 && (
          <EmptyState
            testId="ideas-empty"
            icon={Sparkles}
            title={brandDna ? "Ready to brainstorm" : "Analyze a brand first"}
            hint={brandDna
              ? "Pick a Smart Template or write your own creative direction above, then hit Generate Ideas to draft on-brand concepts."
              : "Use the Brand DNA panel on the left to analyze a website — then come back to generate ideas."}
          />
        )}
        {ideas.map((idea, i) => (
          <IdeaCard
            key={idea.id}
            idea={idea}
            index={i}
            assetType={assetType}
            onChange={updateIdea}
            onRender={renderAssets}
            rendering={renderingIds.has(idea.id)}
            renderCount={renderCounts[idea.id] || 0}
          />
        ))}
      </div>
    </div>
  );
}
